import { fieldReviewReference } from './field-reference-options';

import type { FieldDestination } from './field-overview';
import type { FieldProgressEvidence, FieldProgressStage, FieldSnapshot } from '@mio/shared';

export function fieldProgressSourceDestination(
  snapshot: FieldSnapshot,
  source: Pick<FieldProgressEvidence, 'id' | 'kind'>,
): FieldDestination {
  // Version remains visible in the evidence label; the source view owns its history.
  const id = source.kind === 'decision' ? source.id.replace(/@v\d+$/, '') : source.id;
  if (source.kind === 'source' && id.startsWith('starter:')) return { section: 'build' };
  const decision = snapshot.discovery?.decisions.find((item) => item.id === id);
  const finding = snapshot.discovery?.findings.find((item) => item.id === id);
  if (decision || finding || id === snapshot.discovery?.brief.id)
    return { section: 'engagement', focusId: id };
  const message = snapshot.messages.find((item) => item.id === id);
  if (message)
    return {
      section: 'inbox',
      actorId: message.actorId,
      focusId: id,
      ...(message.meetingId ? { meetingId: message.meetingId } : {}),
    };
  if (snapshot.tickets.some((item) => item.id === id)) return { section: 'board', focusId: id };
  if (snapshot.documents.some((item) => item.id === id))
    return { section: 'documents', focusId: id };
  const review = fieldReviewReference(snapshot, id);
  if (review) return review.destination;
  const pr = snapshot.prs.find(
    (item) => item.id === id || item.reviews.some((entry) => entry.id === id),
  );
  if (pr) return { section: 'reviews', focusId: pr.id };
  const run = snapshot.runs.find((item) => item.id === id);
  if (run) return { section: 'operations', focusId: run.id };
  const disclosure = snapshot.messages.find(
    (item) => item.role === 'colleague' && item.sourceIds.includes(id),
  );
  if (disclosure)
    return {
      section: 'inbox',
      actorId: disclosure.actorId,
      focusId: disclosure.id,
      ...(disclosure.meetingId ? { meetingId: disclosure.meetingId } : {}),
    };
  if (source.kind === 'review') return { section: 'reviews' };
  if (source.kind === 'document') return { section: 'documents', focusId: id };
  if (source.kind === 'ticket') return { section: 'board', focusId: id };
  if (source.kind === 'decision') return { section: 'engagement', focusId: id };
  return { section: 'operations', focusId: id };
}

export const fieldProgressStageDestination: Record<FieldProgressStage, FieldDestination> = {
  agreed: { section: 'engagement' },
  designed: { section: 'documents' },
  built: { section: 'build' },
  verified: { section: 'operations' },
  controls: { section: 'operations' },
  shipped: { section: 'operations' },
};
